// src/components/CartShippingAddress.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Checkbox } from "../components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import AddressAutocomplete from '../components/AddressAutocomplete';
import { useConfig } from '../context/ConfigContext';

const CartShippingAddress = ({ 
  user, 
  selectedAddress, 
  setSelectedAddress, 
  deliveryInstructions, 
  setDeliveryInstructions,
  refreshUser
}) => {
  const [isShippingExpanded, setIsShippingExpanded] = useState(false);
  const [showAddressDialog, setShowAddressDialog] = useState(false);
  const [newAddress, setNewAddress] = useState({
    street: '',
    city: '',
    state: '',
    postalCode: '',
    country: 'Canada'
  });
  const { config } = useConfig();
  
  useEffect(() => {
    // Set default address when user data loads or changes
    if (user && user.addresses && user.addresses.length > 0) {
      // Find the default address if it exists
      const defaultAddress = user.addresses.find(addr => addr.isDefault);
      
      // If there's a default address, select it
      if (defaultAddress) {
        setSelectedAddress(defaultAddress._id ? defaultAddress._id.toString() : `index-${user.addresses.indexOf(defaultAddress)}`);
      } 
      // Otherwise select the first address
      else if (!selectedAddress) {
        setSelectedAddress(user.addresses[0]._id ? user.addresses[0]._id.toString() : 'index-0');
      }
    }
  }, [user, selectedAddress, setSelectedAddress]);

  if (!user) return null;
    
  return (
    <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm mb-6 relative">
      {/* Shipping section header with toggle icon */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center">
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor" style={{ color: config.colors.primary }}>
          <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
        </svg>
          Shipping Address
        </h3>
        
        {/* Toggle caret button */}
        <button 
          onClick={() => setIsShippingExpanded(!isShippingExpanded)}
          className="text-gray-500 hover:text-gray-700 transition-colors focus:outline-none"
          aria-label={isShippingExpanded ? "Collapse shipping section" : "Expand shipping section"}
        >
          <svg 
            xmlns="http://www.w3.org/2000/svg" 
            className={`h-5 w-5 transition-transform duration-200 ${isShippingExpanded ? 'rotate-180' : ''}`} 
            fill="none" 
            viewBox="0 0 24 24" 
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </div>
      
      {/* Collapsible content area */}
      {isShippingExpanded && (
        <>
          {user.addresses && user.addresses.length > 0 ? (
            <RadioGroup 
              value={selectedAddress} 
              onValueChange={setSelectedAddress}
              className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4"
            >
              {user.addresses.map((address, idx) => (
                <div key={idx} 
                className="flex items-start space-x-3 p-4 border rounded-md transition-all duration-200" 
                style={{
                  borderColor: selectedAddress === (address._id ? address._id.toString() : `index-${idx}`) ? config.colors.primary : '#e5e7eb',
                  backgroundColor: selectedAddress === (address._id ? address._id.toString() : `index-${idx}`) ? `${config.colors.primary}10` : 'transparent',
                }}
                onMouseOver={(e) => {
                  if (selectedAddress !== (address._id ? address._id.toString() : `index-${idx}`)) {
                    e.currentTarget.style.borderColor = `${config.colors.primary}80` || '#3B82F6';
                    e.currentTarget.style.backgroundColor = '#f9fafb' ;
                  }
                }}
                onMouseOut={(e) => {
                  if (selectedAddress !== (address._id ? address._id.toString() : `index-${idx}`)) {
                    e.currentTarget.style.borderColor = '#e5e7eb';
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}>
                  <RadioGroupItem 
                    value={address._id ? address._id.toString() : `index-${idx}`}
                    id={`address-${idx}`} 
                    className="mt-1" 
                  />
                  <div className="flex-1">
                    <div className="flex justify-between items-start">
                      <label htmlFor={`address-${idx}`} className="font-medium text-gray-700 cursor-pointer flex flex-col">
                        <span className="text-gray-900">{address.street}</span>
                        <span className="text-gray-600 text-sm">{address.city}, {address.state} {address.postalCode}</span>
                        <span className="text-gray-600 text-sm">{address.country}</span>
                      </label>
                      
                      {/* Default address badge */}
                      {address.isDefault && (
                        <span className="ml-2 text-xs font-medium px-2 py-0.5 rounded" 
                          style={{ 
                            backgroundColor: `${config.colors.primary}20`, 
                            color: config.colors.primary 
                          }}>
                          Default
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </RadioGroup>
          ) : (
            <div className="flex items-center justify-center p-6 bg-gray-50 rounded-md mb-4">
              <div className="text-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-gray-400 mx-auto mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <p className="text-gray-600">No saved addresses.</p>
              </div>
            </div>
          )}
          
          <div className="flex justify-between items-center">
            <Dialog open={showAddressDialog} onOpenChange={setShowAddressDialog}>
              <DialogTrigger asChild>
                <Button variant="outline" className="flex items-center text-blue-600 border-blue-300 hover:bg-blue-50">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Add New Address
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle className="text-xl">Add New Address</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  {/* Address autocomplete implementation */}
                  <AddressAutocomplete 
                    index={0}
                    address={newAddress}
                    onAddressChange={(index, field, value) => {
                      setNewAddress(prev => ({
                        ...prev,
                        [field]: value
                      }));
                    }}
                  />
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="state">Province/State</label>
                      <input
                        id="state"
                        type="text"
                        value={newAddress.state}
                        onChange={(e) => setNewAddress({...newAddress, state: e.target.value})}
                        className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="BC"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="postalCode">Postal Code</label>
                      <input
                        id="postalCode"
                        type="text"
                        value={newAddress.postalCode}
                        onChange={(e) => setNewAddress({...newAddress, postalCode: e.target.value})}
                        className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="V6B 1A1"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="country">Country</label>
                    <select
                      id="country"
                      value={newAddress.country}
                      onChange={(e) => setNewAddress({...newAddress, country: e.target.value})}
                      className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="Canada">Canada</option>
                      <option value="United States">United States</option>
                    </select>
                  </div>
                  
                  {/* Add checkbox for setting as default address */}
                  <div className="flex items-center">
                    <Checkbox 
                      id="isDefault" 
                      checked={newAddress.isDefault || false}
                      onCheckedChange={(checked) => setNewAddress({...newAddress, isDefault: !!checked})}
                      className="mr-2"
                    />
                    <label htmlFor="isDefault" className="text-sm text-gray-700">
                      Set as default shipping address
                    </label>
                  </div>
                  
                  <div className="flex justify-end mt-6 space-x-3">
                    <Button variant="outline" onClick={() => setShowAddressDialog(false)}>Cancel</Button>
                    <Button
                       style={{ 
                        backgroundColor: `${config.colors.primary}`, 
                        color: '#ffffff' 
                      }}
                         onClick={() => {
                      if (user && user._id) {
                        // If setting new address as default, remove default from others
                        let updatedAddresses = [...(user.addresses || [])];
                        
                        if (newAddress.isDefault) {
                          updatedAddresses = updatedAddresses.map(addr => ({
                            ...addr,
                            isDefault: false
                          }));
                        }
                        
                        // Add the new address
                        updatedAddresses.push(newAddress);
                        
                        // Update user profile with the new addresses
                        axios.put('/api/users/profile', {
                          addresses: updatedAddresses
                        }, {
                          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
                        }).then(() => {
                          // Refresh user data
                          refreshUser();
                          setShowAddressDialog(false);
                          setNewAddress({
                            street: '',
                            city: '',
                            state: '',
                            postalCode: '',
                            country: 'Canada',
                            isDefault: false
                          });
                        }).catch(err => {
                          console.error("Error adding address:", err);
                        });
                      }
                    }}>Save Address</Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>
          
          <div className="mt-6 bg-gray-50 p-4 rounded-md">
            <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center" htmlFor="deliveryInstructions">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              Delivery Instructions (Optional)
            </label>
            <Textarea
              id="deliveryInstructions"
              value={deliveryInstructions}
              onChange={(e) => setDeliveryInstructions(e.target.value)}
              placeholder="Special instructions for delivery (e.g., 'Leave at the door', 'Call upon arrival')"
              className="resize-none border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </>
      )}
    </div>
  );
};

export default CartShippingAddress;